import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMerchants } from '../hooks/useMerchant'
import { MerchantDTO } from '../dto/merchant'

export const NONE_MERCHANT = { id: 0, name: 'None', processors: [] }

const MERCHANT_PARAM_NAME = 'merchant'

const addNoneToList = merchants => {
  return [NONE_MERCHANT, ...merchants]
}

interface IMerchantContext {
  merchantList?: MerchantDTO[]
  displayMerchantList?: MerchantDTO[]
  selectedMerchant?: MerchantDTO
  setSelectedMerchant?: (merchant: MerchantDTO) => void
  isMerchantsLoading?: boolean
}

const MerchantContext = createContext<IMerchantContext>({})

export const MerchantProvider = ({ children }) => {
  const navigate = useNavigate()
  const { merchantsData, isMerchantsLoading } = useMerchants()
  const [displayMerchantList, setDisplayMerchantList] = useState<MerchantDTO[]>(
    []
  )
  const [selectedMerchant, setSelectedMerchant] = useState<MerchantDTO>()

  const { search, pathname } = useLocation()
  const searchParams = new URLSearchParams(search)
  const merchantParamId = searchParams.get(MERCHANT_PARAM_NAME)

  const selectMerchant = (merchant: MerchantDTO) => {
    const searchParams = new URLSearchParams(search)
    searchParams.set(MERCHANT_PARAM_NAME, `${merchant.id}`)
    navigate({ pathname, search: searchParams.toString() })
    setSelectedMerchant(merchant)
  }

  useEffect(() => {
    if (!merchantsData) {
      return
    }

    if (merchantsData.length === 1) {
      selectMerchant(merchantsData[0])
      setDisplayMerchantList(merchantsData)
      return
    }

    const listWithNone = addNoneToList(merchantsData)
    setDisplayMerchantList(listWithNone)

    if (merchantParamId) {
      const merchantToSelect = merchantsData.find(
        merchant => merchant.id === +merchantParamId
      )
      if (merchantToSelect) {
        selectMerchant(merchantToSelect)
        return
      }
    }

    selectMerchant(listWithNone[0])
  }, [merchantsData, merchantParamId])

  const contextValue = useMemo(
    () => ({
      merchantList: merchantsData,
      displayMerchantList,
      selectedMerchant,
      isMerchantsLoading,
      setSelectedMerchant: selectMerchant
    }),
    [
      merchantsData,
      displayMerchantList,
      selectedMerchant,
      isMerchantsLoading,
      selectMerchant
    ]
  )

  return (
    <MerchantContext.Provider value={contextValue}>
      {children}
    </MerchantContext.Provider>
  )
}

export const useMerchantContext = () => {
  return useContext(MerchantContext)
}
